///
/// EarthBrowser
///
/// Created by Matt Giger on 9/18/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Enabling/disabling the push channel for this browser: the browser
/// half (permission prompt + PushManager subscription) plus the server
/// half (persisting the subscription for the alert evaluator) as one
/// operation. Used by the placemark bell toggle and the Location Alerts
/// settings UI.
///
import {
	useRegisterPushSubscriptionMutation,
	useUnregisterPushSubscriptionMutation,
} from "@/redux/pushSubscriptionsApi"
import { ensurePushSubscription, unsubscribePush } from "@/utils/webPush"
import { useCallback } from "react"

/** Short human label for the management UI ("Chrome · macOS") */
function browserLabel(): string {
	const ua = navigator.userAgent
	const browser = ua.includes('Edg/') ? "Edge"
		: ua.includes('OPR/') ? "Opera"
			: ua.includes('Firefox/') ? "Firefox"
				: ua.includes('Chrome/') ? "Chrome"
					: ua.includes('Safari/') ? "Safari" : "Browser"
	const os = ua.includes('Mac OS X') ? "macOS"
		: ua.includes('Windows') ? "Windows"
			: ua.includes('Android') ? "Android"
				: /iPhone|iPad/.test(ua) ? "iOS"
					: ua.includes('Linux') ? "Linux" : ""
	return os ? `${browser} · ${os}` : browser
}

export function usePushChannel() {
	const [registerSubscription] = useRegisterPushSubscriptionMutation()
	const [unregisterSubscription] = useUnregisterPushSubscriptionMutation()

	/**
	 * Prompt (user gesture required), subscribe this browser, and register
	 * with the server. Idempotent.
	 */
	const enablePush = useCallback(async (): Promise<"subscribed" | "denied" | "unsupported"> => {
		const result = await ensurePushSubscription()
		if (result.status === "subscribed") {
			try {
				await registerSubscription({
					endpoint: result.endpoint,
					keys: { p256dh: result.p256dh, auth: result.auth },
					user_agent: browserLabel(),
				}).unwrap()
			} catch (error) {
				console.warn("[push] Server registration failed:", error)
			}
		}
		return result.status
	}, [registerSubscription])

	/** Unsubscribe this browser and remove the server registration */
	const disablePush = useCallback(async (): Promise<void> => {
		const endpoint = await unsubscribePush()
		if (endpoint) {
			try {
				await unregisterSubscription(endpoint).unwrap()
			} catch (error) {
				console.warn("[push] Server unregistration failed:", error)
			}
		}
	}, [unregisterSubscription])

	return { enablePush, disablePush }
}
